import { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Layout, { stepHeaderWrapperClass } from "./layout/Layout";
import BookingStepHeader from "./bookingForm/BookingStepHeader";
import type { StepInfo } from "./bookingForm/BookingStepHeader";
import BookingInfoInline from "./bookingForm/BookingInfoInline";
import MobileStepNav from "./bookingForm/MobileStepNav";
import { useBooking } from "./hooks/useBooking";
import useWindowWidthThreshold from "./hooks/useWindowWidthThreshold";
import { useFormattedBookingInfo } from "./hooks/useFormattedBookingInfo";
import { usePreorderState } from "./hooks/usePreorderState";
import { useMultiPersonOrdering } from "./hooks/useMultiPersonOrdering";
import { useInFlight } from "./hooks/useInFlight";
import { Menu } from "./preOrders/menu";
import ConfirmClientDetails from "./preOrders/confirmClientDetails";
import Basket from "./preOrders/basket";
import BasketFooter from "./preOrders/basketFooter";
import DietaryModal from "./preOrders/dietaryModal";
import type {
  CustomerPreOrder,
  CustomerPreOrderItemUpdateRequest,
  PreOrderItem,
  TaxBreakdownEntry,
} from "../../common/jsonRpcTypes";
import "../styles/pre-orders.scss";
import "../styles/multi-person-ordering.scss";
import PaymentConfirmationLayout from "./confirmation/PaymentConfirmationLayout";
import OrderSummary from "./orderSummary";
import { isAnyOrderedMenuPaidByGroupMember } from "../utils/preorderSettingsUtils";
import { InfoCircle } from "./icons";
import AlertBanner from "./shared/AlertBanner";
import DietaryRequirementsButton from "./shared/DietaryRequirementsButton";
import DietarySummary from "./shared/DietarySummary";
import PageStateGuard from "./shared/PageStateGuard";
import { formatDate, formatTime } from "../../utils/countryLocaleMap";
import PreOrderUnavailablePage from "./PreOrderUnavailablePage";
import { hasBookingTakenPlace } from "../utils/pastBooking";
import { useLegacyPreorderRedirect } from "../utils/redirectToOldExperience";
import { canUseStripeConnect } from "../utils/paymentProcessorUtils";
import {
  dietaryRequirementsToCustomerNotes,
  isMultiPersonOrderingEnabled,
  resolveExcessNameValidity,
  withSharedOrderNote,
  type MenuLimits,
} from "./preOrders/preOrderUtils";
import MultiPersonNameInput from "./preOrders/MultiPersonNameInput";
import { PreorderOffersInfo } from "./preOrders/PreorderOffersInfo";
import { PreorderExpiryWarning } from "./preOrders/PreorderExpiryWarning";
import { PreorderProvider, type PreorderContextValue } from "./context/PreorderContext";

interface PlaceYourOrderProps {
  bookingId: string;
  orderId?: string;
  signature?: string;
  manageYourOrderUrl?: string;
}

interface StepHeaderProps {
  readonly venueName?: string;
  readonly currentStage: string;
  readonly steps: StepInfo[];
  readonly bookingDate?: string;
  readonly bookingTime?: string;
  readonly covers?: string;
  readonly bookingTypeName?: string;
  readonly onBack?: () => void;
}

/**
 * StepHeader — the venue/step header shown on the place-your-order flow:
 * the desktop BookingStepHeader plus the BookingInfoInline summary. Shared by the
 * guard screens and the main flow to avoid duplicating the markup.
 */
function StepHeader({
  venueName,
  currentStage,
  steps,
  bookingDate,
  bookingTime,
  covers,
  bookingTypeName,
  onBack,
}: StepHeaderProps) {
  return (
    <>
      <div className={stepHeaderWrapperClass}>
        <BookingStepHeader venueName={venueName} currentStage={currentStage} steps={steps} onBack={onBack} />
      </div>
      <BookingInfoInline
        venueName={venueName}
        bookingDate={bookingDate}
        bookingTime={bookingTime}
        covers={covers}
        bookingTypeName={bookingTypeName}
      />
    </>
  );
}

interface AsideContentProps {
  readonly bookingId: string;
  readonly customerPreOrder: CustomerPreOrder | undefined;
  readonly setForNames: React.Dispatch<React.SetStateAction<string[]>>;
  readonly preservedPreOrderItems:
    | {
        items: PreOrderItem[];
        totalCost: number;
        shouldHidePrices: boolean;
        hidePricesByMenu: Record<string, boolean>;
        serviceChargeAmount?: number;
        serviceChargePercentage?: number;
        totalCostWithService?: number;
        taxInclusive?: boolean;
        taxBreakdown?: TaxBreakdownEntry[];
        totalNet?: number;
        totalGross?: number;
        taxLabel?: string;
      }
    | undefined;
  readonly handleSetOrderingFor: (index: number) => void;
  readonly handleRemoveName: (index: number) => Promise<void>;
  readonly handleNamesChanged: (names: string[]) => Promise<void>;
  readonly canChangeServiceCharge?: boolean;
  readonly onServiceChargeChange?: (percentage: number) => Promise<boolean>;
  readonly showSuccess: boolean;
  readonly showConfirmClientDetails: boolean;
  readonly detailsConfirmed: boolean;
  readonly goToNextStep: () => void;
  readonly isOrderValid: boolean;
  readonly requiredPaymentAmount?: number;
  readonly menuLimits?: MenuLimits;
  readonly onMaxItemsError?: (menuId: string, errorMessage: string) => void;
  readonly shouldHidePrices: boolean;
  readonly bookingData: PreorderContextValue["bookingData"];
  readonly onCacheExpired: () => void | Promise<void>;
  readonly isCacheExpired: boolean;
  readonly resyncKey?: number;
}

function AsideContent(props: AsideContentProps): React.ReactNode {
  const { customerPreOrder, showConfirmClientDetails } = props;

  let mainContent: React.ReactNode = null;
  let footerContent: React.ReactNode = null;

  if (showConfirmClientDetails) {
    mainContent = (
      <OrderSummary
        preorder={customerPreOrder}
        shouldHidePrices={props.preservedPreOrderItems?.shouldHidePrices ?? props.shouldHidePrices}
        canChangeServiceCharge={props.canChangeServiceCharge}
        onServiceChargeChange={props.onServiceChargeChange}
        requiredPaymentAmount={props.requiredPaymentAmount}
        allowServiceChargeChange={!props.detailsConfirmed}
      />
    );
  } else if (customerPreOrder) {
    mainContent = (
      <Basket
        bookingId={props.bookingId}
        setForNames={props.setForNames}
        onSetOrderingFor={props.handleSetOrderingFor}
        onRemoveName={props.handleRemoveName}
        onNamesChanged={props.handleNamesChanged}
        menuLimits={props.menuLimits}
        onMaxItemsError={props.onMaxItemsError}
        totalCost={customerPreOrder.totalCost}
        totalCostWithService={customerPreOrder.totalCostWithService}
        serviceChargeAmount={customerPreOrder.serviceChargeAmount}
        serviceChargePercentage={customerPreOrder.serviceChargePercentage}
        isCacheExpired={props.isCacheExpired}
      />
    );
  }

  // Add footer to aside if applicable
  if (!props.showSuccess && customerPreOrder && !showConfirmClientDetails) {
    footerContent = (
      <BasketFooter
        items={customerPreOrder.items}
        totalCost={customerPreOrder.totalCost}
        totalCostWithService={customerPreOrder.totalCostWithService}
        goToNextStep={props.goToNextStep}
        itemsCount={customerPreOrder.items?.length ?? 0}
        isOrderValid={props.isOrderValid}
        serviceChargeAmount={customerPreOrder.serviceChargeAmount}
        serviceChargePercentage={customerPreOrder.serviceChargePercentage}
        requireBasketItems
      />
    );
  }

  if (!mainContent && !footerContent) {
    return null;
  }

  return (
    <>
      {mainContent}
      <PreorderExpiryWarning
        bookingId={props.bookingId}
        active={!props.showSuccess && !!customerPreOrder}
        onExpired={props.onCacheExpired}
        resyncKey={props.resyncKey}
      />
      {footerContent}
    </>
  );
}

function detectManageYourOrderSource(): boolean {
  if (document.referrer) {
    return document.referrer.includes("manage-your-order");
  }

  const urlParams = new URLSearchParams(window.location.search);
  const source = urlParams.get("source") ?? urlParams.get("from") ?? urlParams.get("ref");
  if (source === "manage-your-order" || source === "manage") {
    return true;
  }

  try {
    const storedSource = sessionStorage.getItem("navigation_source");
    if (storedSource === "manage-your-order") {
      sessionStorage.removeItem("navigation_source");
      return true;
    }
  } catch (e) {
    console.log("SessionStorage not available:", e);
  }

  return false;
}

interface PlaceYourOrderGuardProps {
  readonly loading: boolean;
  readonly isLoadingPreorderData: boolean;
  readonly isRedirecting: boolean;
  readonly error: string | null;
  /** DMN's own reason for refusing the booking, shown as it stands. */
  readonly refusalMessage: string | null;
  /** The booking has run its course, so it takes no further pre-orders. */
  readonly bookingIsPast: boolean;
  readonly bookingData: any;
  readonly preorderLimitMessage: string | null;
  readonly isPreorderUnavailable: boolean;
  readonly preorderUnavailableMessage: string | null;
  readonly isPreorderComplete: boolean;
  readonly customerPreorderComplete?: boolean;
  readonly t: (key: string, defaultValue: string) => string;
  readonly venueName?: string;
  readonly formattedDate?: string;
  readonly formattedTime?: string;
  readonly covers?: string;
  readonly bookingTypeName?: string;
  readonly steps: StepInfo[];
  readonly currentStage: string;
}

interface SuccessContentProps {
  readonly bookingData: any;
  readonly confirmedAmount: number;
  readonly preservedPreOrderItems:
    | {
        items: PreOrderItem[];
        totalCost: number;
        shouldHidePrices: boolean;
        hidePricesByMenu: Record<string, boolean>;
        serviceChargeAmount?: number;
        serviceChargePercentage?: number;
        totalCostWithService?: number;
        taxInclusive?: boolean;
        taxBreakdown?: TaxBreakdownEntry[];
        totalNet?: number;
        totalGross?: number;
        taxLabel?: string;
      }
    | undefined;
  readonly confirmedEmail: string;
  readonly shouldHidePrices: boolean;
  readonly isFromManageYourOrder: boolean;
  readonly signature?: string;
  readonly manageYourOrderUrl?: string;
}

function SuccessContent({
  bookingData,
  confirmedAmount,
  preservedPreOrderItems,
  confirmedEmail,
  shouldHidePrices,
  isFromManageYourOrder,
  signature,
  manageYourOrderUrl,
}: SuccessContentProps) {
  if (!bookingData) return null;

  const formattedDate =
    bookingData.date && typeof bookingData.date === "string"
      ? formatDate(bookingData.date, bookingData.venue.country_code ?? "GB", "short")
      : "";

  const formattedTime =
    bookingData.time && bookingData.date && typeof bookingData.date === "string"
      ? formatTime(bookingData.time, bookingData.date, bookingData.venue.country_code ?? "GB")
      : bookingData.time;

  return (
    <PaymentConfirmationLayout
      venueName={bookingData.venue.title}
      bookingRef={bookingData.bookingRef ?? bookingData.bookingId ?? ""}
      depositTotal={confirmedAmount || Number(bookingData.depositTotal ?? 0)}
      isAuthentication={false}
      partySize={bookingData.guestCount}
      date={formattedDate}
      time={formattedTime}
      location={bookingData.venue.title}
      preOrderItems={preservedPreOrderItems?.items}
      currency={bookingData.venue.currency}
      countryCode={bookingData.venue.country_code}
      mode="successWithDetails"
      shouldHidePrices={preservedPreOrderItems?.shouldHidePrices ?? shouldHidePrices}
      email={confirmedEmail}
      hidePricesByMenu={preservedPreOrderItems?.hidePricesByMenu}
      serviceChargeAmount={preservedPreOrderItems?.serviceChargeAmount}
      serviceChargePercentage={preservedPreOrderItems?.serviceChargePercentage}
      totalCostWithService={preservedPreOrderItems?.totalCostWithService}
      taxInclusive={preservedPreOrderItems?.taxInclusive}
      taxBreakdown={preservedPreOrderItems?.taxBreakdown}
      totalNet={preservedPreOrderItems?.totalNet}
      totalGross={preservedPreOrderItems?.totalGross}
      taxLabel={preservedPreOrderItems?.taxLabel}
      isFromManageYourOrder={isFromManageYourOrder}
      bookingId={bookingData.bookingId}
      signature={signature}
      manageYourOrderUrl={manageYourOrderUrl}
      featureName="Preorder"
      userId={bookingData.user?.user_id ?? null}
      customerName={bookingData.user?.user_name ?? null}
      bookingType={bookingData.bookingTypeName}
    />
  );
}

/**
 * Show a guest-facing notice about the booking itself, as an informational
 * banner. The page chrome needs the booking, which a booking DMN refused never
 * provides, so the notice falls back to the chrome-less banner PageStateGuard
 * renders.
 */
function renderBookingNotice(message: string, header: StepHeaderProps, bookingData: unknown): React.ReactNode {
  if (!bookingData) {
    return (
      <PageStateGuard loading={false} error={null} refusalMessage={message} data={null} showHeader>
        {null}
      </PageStateGuard>
    );
  }

  return (
    <Layout
      showHeader
      main={
        <>
          <StepHeader {...header} />
          <div className="app-main">
            <AlertBanner variant="warning" icon={<InfoCircle color="#9a3412" size={16} />} className="info-alert">
              {message}
            </AlertBanner>
          </div>
        </>
      }
      aside={null}
    />
  );
}

function renderPlaceYourOrderGuard(props: PlaceYourOrderGuardProps): React.ReactNode | null {
  if (props.loading || props.isLoadingPreorderData || props.isRedirecting) {
    return (
      <PageStateGuard loading error={null} data={props.bookingData} showHeader>
        {null}
      </PageStateGuard>
    );
  }

  const header: StepHeaderProps = {
    venueName: props.venueName,
    currentStage: props.currentStage,
    steps: props.steps,
    bookingDate: props.formattedDate,
    bookingTime: props.formattedTime,
    covers: props.covers,
    bookingTypeName: props.bookingTypeName,
  };

  // A fault is a fault. The booking and the pre-order data load independently,
  // so a booking that failed to load can coexist with anything Collins says
  // about pre-ordering — and reporting the failure keeps it recoverable rather
  // than presenting it as a settled outcome. Only a refusal is expected, and it
  // is handled with the booking's own state below.
  if (props.error) {
    return (
      <PageStateGuard loading={false} error={props.error} data={props.bookingData} showHeader>
        {null}
      </PageStateGuard>
    );
  }

  // Collins' own account of why pre-ordering is off comes next; the booking's
  // state only explains a refusal Collins said nothing about.
  if (props.preorderLimitMessage) {
    return renderBookingNotice(props.preorderLimitMessage, header, props.bookingData);
  }

  if (props.isPreorderUnavailable) {
    return <PreOrderUnavailablePage message={props.preorderUnavailableMessage ?? undefined} />;
  }

  if (props.isPreorderComplete || props.customerPreorderComplete) {
    return renderBookingNotice(
      props.t("preorders_closed", "Pre-orders for this booking are now closed."),
      header,
      props.bookingData,
    );
  }

  if (props.bookingIsPast) {
    return renderBookingNotice(
      props.t("booking_past_no_preorders", "This booking has already taken place, so no more pre-orders can be added."),
      header,
      props.bookingData,
    );
  }

  if (props.refusalMessage || !props.bookingData) {
    return (
      <PageStateGuard
        loading={false}
        error={null}
        refusalMessage={props.refusalMessage}
        data={props.bookingData}
        showHeader
      >
        {null}
      </PageStateGuard>
    );
  }

  return null;
}

interface PreOrderMenuViewProps {
  readonly showConfirmClientDetails: boolean;
  readonly showDietaryModal: boolean;
  readonly setShowDietaryModal: (show: boolean) => void;
  readonly dietarySave: ReturnType<typeof useInFlight>;
  readonly detailsConfirmed: boolean;
  readonly setDetailsConfirmed: (value: boolean) => void;
  readonly customerPreOrder: CustomerPreOrder | undefined;
  readonly setCustomerPreOrder: React.Dispatch<React.SetStateAction<CustomerPreOrder | undefined>>;
  readonly bookingData: any;
  readonly forNames: string[];
  readonly multipleNames: boolean;
  readonly editNames: boolean;
  readonly addName: string;
  readonly addNameError: string;
  readonly namesExceedGuests: boolean;
  readonly handleMultipleNamesOptionChange: (checked: boolean) => void;
  readonly handleAddName: () => void;
  readonly setAddName: (name: string) => void;
  readonly handleValidationChange: (isValid: boolean) => void;
  readonly setMenuLimits: React.Dispatch<React.SetStateAction<MenuLimits>>;
  readonly externalValidationErrors: { [menuId: string]: string };
  readonly savePreorders: (details: { name: string; email: string; customerNotes?: string }) => Promise<void>;
  readonly paidByGroupMember: boolean;
  readonly canShowStripeConnect: boolean;
  readonly handlePaymentSuccess: (email?: string, amount?: number) => void;
  readonly confirmDisabled: boolean;
  readonly t: (key: string, defaultValue: string) => string;
}

function PreOrderMenuView(props: PreOrderMenuViewProps) {
  const {
    showConfirmClientDetails,
    showDietaryModal,
    setShowDietaryModal,
    dietarySave,
    detailsConfirmed,
    setDetailsConfirmed,
    customerPreOrder,
    setCustomerPreOrder,
    bookingData,
    forNames,
    multipleNames,
    editNames,
    addName,
    addNameError,
    namesExceedGuests,
    handleMultipleNamesOptionChange,
    handleAddName,
    setAddName,
    handleValidationChange,
    setMenuLimits,
    externalValidationErrors,
    savePreorders,
    paidByGroupMember,
    canShowStripeConnect,
    handlePaymentSuccess,
    confirmDisabled,
    t,
  } = props;

  const dietaryCount = (customerPreOrder?.dietaryRequirements ?? []).reduce(
    (total, req) => total + (req.diet_types?.length ?? 0) + (req.allergies?.length ?? 0),
    0,
  );

  const showDietaryButton = !bookingData.venue.hideDietaryRequirements && !showConfirmClientDetails;

  // Venues can switch off the shareable pre-order link, in which case the
  // multi-person ordering controls must not be offered and any names cached
  // from a previous session are ignored.
  const multiPersonOrderingEnabled = isMultiPersonOrderingEnabled(customerPreOrder);
  const showMultiPersonNameInput = !showConfirmClientDetails && multiPersonOrderingEnabled;
  const effectiveForNames = multiPersonOrderingEnabled ? forNames : [];

  let menuContent: React.ReactNode = null;
  if (showConfirmClientDetails) {
    menuContent = (
      <ConfirmClientDetails
        customerPreOrder={customerPreOrder}
        customerNotes={dietaryRequirementsToCustomerNotes(customerPreOrder?.dietaryRequirements)}
        onCustomerNotesChange={(notes) =>
          setCustomerPreOrder((prev) =>
            prev ? { ...prev, dietaryRequirements: withSharedOrderNote(prev.dietaryRequirements, notes) } : prev,
          )
        }
        onSubmit={savePreorders}
        bookingData={bookingData}
        paidByGroupMember={paidByGroupMember}
        useStripeConnect={canShowStripeConnect}
        onPaymentSuccess={handlePaymentSuccess}
        isConfirmed={detailsConfirmed}
        setIsConfirmed={setDetailsConfirmed}
        confirmDisabled={confirmDisabled}
      />
    );
  } else if (customerPreOrder) {
    menuContent = (
      <Menu
        onValidationChange={handleValidationChange}
        onMenuLimitsChange={setMenuLimits}
        externalValidationErrors={externalValidationErrors}
      />
    );
  }

  return (
    <div className="app-main full-height-mobile">
      <div className="gcw:mb-2">
        <div className="gcw:flex gcw:justify-between gcw:items-center">
          <h1 className="payment-title">{t("pre_orders_title", "Pre-orders Menu")}</h1>
        </div>
        <DietarySummary dietaryRequirements={customerPreOrder?.dietaryRequirements} forNames={effectiveForNames} />
        <PreorderOffersInfo
          closeDate={customerPreOrder?.customer_preorder_settings?.close_date}
          countryCode={bookingData.venue.country_code ?? "GB"}
          venueTimezone={bookingData.venue.timezone}
        />
      </div>
      {showMultiPersonNameInput && (
        <MultiPersonNameInput
          multipleNames={multipleNames}
          editNames={editNames}
          addName={addName}
          addNameError={addNameError}
          namesExceedGuests={namesExceedGuests}
          onToggle={handleMultipleNamesOptionChange}
          onAddName={handleAddName}
          onAddNameChange={setAddName}
        />
      )}
      {showDietaryButton && (
        <div className="gcw:mb-bs-3">
          <div>
            <DietaryRequirementsButton
              dietaryCount={dietaryCount}
              onClick={(e) => {
                e.preventDefault();
                setShowDietaryModal(true);
              }}
            />
          </div>
        </div>
      )}
      {showDietaryModal && (
        <DietaryModal
          show={showDietaryModal}
          onClose={() => setShowDietaryModal(false)}
          forNames={effectiveForNames}
          customerPreOrder={customerPreOrder}
          inFlight={dietarySave}
          updateDietaryRequirements={(dietaryRequirements) => {
            setCustomerPreOrder((prev) => (prev ? { ...prev, dietaryRequirements } : prev));
          }}
        />
      )}
      <div className="pre-order-form">{menuContent}</div>
      {!showConfirmClientDetails && (
        <span className="kcal">{t("pre_order_kcal_info", "Adults require around 2000 kcal a day")}</span>
      )}
    </div>
  );
}

function applyLocalPreOrderNames(
  localPreOrder: any,
  setForNames: (names: string[]) => void,
  setMultipleNames: (v: boolean) => void,
  setEditNames: (v: boolean) => void,
): void {
  const names = localPreOrder?.forNames;
  if (!Array.isArray(names)) return;
  const hasNames = names.length > 0;
  setForNames(names);
  setMultipleNames(hasNames);
  setEditNames(hasNames);
}

function getMainClassName(showSuccess: boolean, showConfirmClientDetails: boolean, isDesktop: boolean): string {
  const isConfirmStage = showSuccess || showConfirmClientDetails;
  return isConfirmStage && !isDesktop ? "confirm-fullscreen-mobile" : "";
}

function resolveAsideContent(
  showSuccess: boolean,
  isDesktop: boolean,
  hasBasketItems: boolean,
  asideContent: React.ReactNode,
): React.ReactNode {
  if (showSuccess) return null;
  // On mobile (basket not on the side), only show the basket when there are items.
  if (!isDesktop && !hasBasketItems) return null;
  return asideContent;
}

/**
 * Hierarchical back navigation for the confirm step: if the user has confirmed
 * their details and is on the payment form, step back to the editable details view;
 * otherwise leave the confirm step and return to the menu.
 */

interface BuildPreorderContextParams {
  readonly bookingData: PreorderContextValue["bookingData"] | null | undefined;
  readonly customerPreOrder: PreorderContextValue["customerPreOrder"];
  readonly customerPreOrders: PreorderContextValue["customerPreOrders"] | undefined;
  readonly currency: string;
  readonly countryCode: string;
  readonly hidePricesByMenu: PreorderContextValue["hidePricesByMenu"];
  readonly shouldHidePrices: boolean;
  readonly forNames: string[];
  readonly orderingFor: number;
  readonly multipleNames: boolean;
  readonly updateCustomerPreOrder: PreorderContextValue["updateCustomerPreOrder"];
  readonly isItemPending: PreorderContextValue["isItemPending"];
}

function buildPreorderContext(params: BuildPreorderContextParams): PreorderContextValue | undefined {
  if (!params.bookingData) return undefined;
  // Names cached from a previous session are ignored once the venue disables the
  // shareable link, so the guest is never left with a per-person basket they
  // have no way to turn off.
  const multiPersonOrderingEnabled = isMultiPersonOrderingEnabled(params.customerPreOrder);
  return {
    bookingData: params.bookingData,
    customerPreOrder: params.customerPreOrder,
    customerPreOrders: params.customerPreOrders ?? {},
    currency: params.currency,
    countryCode: params.countryCode,
    hidePricesByMenu: params.hidePricesByMenu,
    shouldHidePrices: params.shouldHidePrices,
    forNames: multiPersonOrderingEnabled ? params.forNames : [],
    orderingFor: params.orderingFor,
    multipleNames: multiPersonOrderingEnabled && params.multipleNames,
    multiPersonOrderingEnabled,
    updateCustomerPreOrder: params.updateCustomerPreOrder,
    isItemPending: params.isItemPending,
    linkedBookings: [],
    bookingTypeName: undefined,
    bookingTypeId: undefined,
  };
}

/**
 * The date to judge the booking by. DMN reports it on the pre-order payload
 * too, which is the only copy left when it refuses the booking itself.
 */
function bookingDateOf(bookingData: { date?: unknown } | null, customerPreOrder: CustomerPreOrder | undefined) {
  if (typeof bookingData?.date === "string") return bookingData.date;
  return customerPreOrder?.booking?.date;
}

const PlaceYourOrder: React.FC<PlaceYourOrderProps> = ({ bookingId, orderId, signature, manageYourOrderUrl }) => {
  const { bookingData, loading, error, unavailableMessage } = useBooking(bookingId);
  const isDesktop = useWindowWidthThreshold(1024);
  const [showConfirmClientDetails, setShowConfirmClientDetails] = useState(false);
  const [isCacheExpired, setIsCacheExpired] = useState(false);
  const [resyncKey, setResyncKey] = useState(0);
  // Tracks whether the user has confirmed their details and moved on to the payment
  // form within the confirm step. Lifted from ConfirmClientDetails so the header/mobile
  // back button can step back from the payment form to the editable details view
  // instead of leaving the confirm step entirely.
  const [detailsConfirmed, setDetailsConfirmed] = useState(false);
  const { t } = useTranslation();
  const [showDietaryModal, setShowDietaryModal] = useState(false);
  const dietarySave = useInFlight();
  const [isFromManageYourOrder, setIsFromManageYourOrder] = useState<boolean>(false);
  const isRedirecting = useLegacyPreorderRedirect(bookingData, bookingId);

  // Shared state for multi-person ordering (lifted here so both hooks can access it)
  const [forNames, setForNames] = useState<string[]>([]);
  const [multipleNames, setMultipleNames] = useState(false);
  const [editNames, setEditNames] = useState(false);

  const handleLocalPreOrderLoaded = useCallback(
    (localPreOrder: any) => applyLocalPreOrderNames(localPreOrder, setForNames, setMultipleNames, setEditNames),
    [],
  );

  const {
    customerPreOrder,
    customerPreOrders,
    setCustomerPreOrder,
    setCustomerPreOrders,
    preservedPreOrderItems,
    isLoadingPreorderData,
    isPreorderComplete,
    isPreorderUnavailable,
    preorderUnavailableMessage,
    preorderLimitMessage,
    menuLimits,
    setMenuLimits,
    externalValidationErrors,
    originalServiceChargePercentage,
    confirmedEmail,
    confirmedAmount,
    showSuccess,
    updateCustomerPreOrder,
    isItemPending,
    refreshCustomerPreOrder,
    reloadServerBasket,
    handleServiceChargeChange,
    handleValidationChange,
    handleMaxItemsError,
    handlePaymentSuccess,
    savePreorders,
    isOrderValid: isBasketValid,
    hidePricesByMenu,
    shouldHidePrices,
    paidByGroupMember,
  } = usePreorderState({
    bookingId,
    orderId,
    bookingData,
    forNames,
    onLocalPreOrderLoaded: handleLocalPreOrderLoaded,
  });

  // The booking's own state gates pre-ordering. Collins' status says where a
  // booking sits in the venue's workflow, not whether the visit is over, so a
  // booking DMN refused outright is the only signal there is.
  const bookingIsPast = hasBookingTakenPlace({
    date: bookingDateOf(bookingData, customerPreOrder),
    refused: !!unavailableMessage,
  });

  const handleCacheExpired = useCallback(async () => {
    setIsCacheExpired(true);
    setCustomerPreOrders({});
    setShowConfirmClientDetails(false);
    setDetailsConfirmed(false);
    await refreshCustomerPreOrder(false);
  }, [refreshCustomerPreOrder, setCustomerPreOrders]);

  // Resyncs the expiry warning and clears the cache-expired banner once the user
  // successfully adds items again.
  const dispatchPreOrderUpdate = useCallback(
    async (request: CustomerPreOrderItemUpdateRequest): Promise<boolean> => {
      const ok = await updateCustomerPreOrder(request);
      if (ok) {
        setResyncKey((key) => key + 1);
        setIsCacheExpired(false);
      }
      return ok;
    },
    [updateCustomerPreOrder],
  );

  const {
    orderingFor,
    addName,
    setAddName,
    addNameError,
    namesExceedGuests,
    handleMultipleNamesOptionChange,
    handleAddName,
    handleRemoveName,
    handleSetOrderingFor,
    syncDietaryRequirementNames,
  } = useMultiPersonOrdering({
    bookingId,
    customerPreOrder,
    setCustomerPreOrder,
    updateCustomerPreOrder: dispatchPreOrderUpdate,
    refreshCustomerPreOrder,
    reloadServerBasket,
    forNames,
    setForNames,
    multipleNames,
    setMultipleNames,
    editNames,
    setEditNames,
    guestCount: bookingData?.guestCount,
  });

  const { isOrderValid, showNamesExceedGuests } = resolveExcessNameValidity(
    isBasketValid,
    namesExceedGuests,
    isMultiPersonOrderingEnabled(customerPreOrder),
  );

  useEffect(() => {
    setIsFromManageYourOrder(detectManageYourOrderSource());
  }, []);

  // Clear showConfirmClientDetails when payment succeeds
  useEffect(() => {
    if (showSuccess) {
      setShowConfirmClientDetails(false);
      setDetailsConfirmed(false);
    }
  }, [showSuccess]);

  const goToNextStep = useCallback(() => {
    if (isCacheExpired) return;

    setDetailsConfirmed(false);
    setShowConfirmClientDetails(true);
  }, [isCacheExpired]);

  // The guest may only adjust the discretionary service charge when they contribute
  // to it. On /place-your-order that means at least one menu they ordered from is
  // paid by the group member — an order drawn only from pay-at-venue or
  // lead-booker-paid menus is not charged to them, so the rate stays as configured.
  const canChangeServiceCharge =
    showConfirmClientDetails &&
    (customerPreOrder?.items?.length ?? 0) > 0 &&
    originalServiceChargePercentage !== null &&
    originalServiceChargePercentage > 0 &&
    isAnyOrderedMenuPaidByGroupMember(customerPreOrder);

  // Hooks must be called before any early returns to satisfy React's rules of hooks.
  // The context value is only consumed when bookingData is available (after guard checks).
  const currency = bookingData?.venue?.currency ?? "GBP";
  const countryCode = bookingData?.venue?.country_code ?? "GB";

  const preorderContextValue = useMemo(
    () =>
      buildPreorderContext({
        bookingData,
        customerPreOrder,
        customerPreOrders,
        currency,
        countryCode,
        hidePricesByMenu,
        shouldHidePrices,
        forNames,
        orderingFor,
        multipleNames,
        updateCustomerPreOrder: dispatchPreOrderUpdate,
        isItemPending,
      }),
    [
      bookingData,
      customerPreOrder,
      customerPreOrders,
      currency,
      countryCode,
      hidePricesByMenu,
      shouldHidePrices,
      forNames,
      orderingFor,
      multipleNames,
      dispatchPreOrderUpdate,
      isItemPending,
    ],
  );

  // Step definitions for the place-your-order flow (reusing BookingStepHeader pattern)
  const placeOrderSteps: StepInfo[] = useMemo(
    () => [
      { name: "Pre-order", stage: "pre_order" },
      { name: "Confirm", stage: "confirm" },
    ],
    [],
  );
  const isConfirmStage = showSuccess || showConfirmClientDetails;
  const currentStage = isConfirmStage ? "confirm" : "pre_order";

  // Format booking details for BookingInfoInline (reusing the same pattern as /book)
  const { formattedDate, formattedTime, formattedCovers } = useFormattedBookingInfo({
    coreBookingDetails: {
      date: bookingData?.date && typeof bookingData.date === "string" ? bookingData.date : null,
      time: bookingData?.time,
      num_people: bookingData?.guestCount,
    },
    countryCode,
  });

  const guardContent = renderPlaceYourOrderGuard({
    loading,
    isLoadingPreorderData,
    isRedirecting,
    error,
    refusalMessage: unavailableMessage,
    bookingIsPast,
    bookingData,
    preorderLimitMessage,
    isPreorderUnavailable,
    preorderUnavailableMessage,
    isPreorderComplete,
    customerPreorderComplete: customerPreOrder?.customer_preorder_settings?.complete,
    t,
    venueName: bookingData?.venue?.title,
    formattedDate,
    formattedTime,
    covers: formattedCovers,
    bookingTypeName: bookingData?.bookingTypeName,
    steps: placeOrderSteps,
    currentStage,
  });
  if (guardContent) return guardContent;
  if (!bookingData) return null;
  if (!preorderContextValue) return null;

  const canShowStripeConnect = canUseStripeConnect(bookingData);

  const asideContent = (
    <AsideContent
      bookingId={bookingId}
      customerPreOrder={customerPreOrder}
      bookingData={bookingData}
      setForNames={setForNames}
      preservedPreOrderItems={preservedPreOrderItems}
      handleSetOrderingFor={handleSetOrderingFor}
      handleRemoveName={handleRemoveName}
      handleNamesChanged={syncDietaryRequirementNames}
      canChangeServiceCharge={canChangeServiceCharge}
      onServiceChargeChange={handleServiceChargeChange}
      showSuccess={showSuccess}
      showConfirmClientDetails={showConfirmClientDetails}
      detailsConfirmed={detailsConfirmed}
      goToNextStep={goToNextStep}
      isOrderValid={isOrderValid}
      requiredPaymentAmount={customerPreOrder?.requiredPaymentAmount}
      menuLimits={menuLimits}
      onMaxItemsError={handleMaxItemsError}
      shouldHidePrices={shouldHidePrices}
      onCacheExpired={handleCacheExpired}
      isCacheExpired={isCacheExpired}
      resyncKey={resyncKey}
    />
  );

  const hasBasketItems = (customerPreOrder?.items?.length ?? 0) > 0;
  const asideValue = resolveAsideContent(showSuccess, isDesktop, hasBasketItems, asideContent);

  const onBack = showConfirmClientDetails
    ? () => {
        if (detailsConfirmed) {
          setDetailsConfirmed(false);
        } else {
          setShowConfirmClientDetails(false);
        }
      }
    : undefined;

  const mobileStepNav = showSuccess ? null : (
    <MobileStepNav
      currentStepInfo={placeOrderSteps.find((s) => s.stage === currentStage)}
      currentStepNumber={placeOrderSteps.findIndex((s) => s.stage === currentStage) + 1}
      totalStepCount={placeOrderSteps.length}
      onBack={onBack}
    />
  );

  const mainClassName = getMainClassName(showSuccess, showConfirmClientDetails, isDesktop);

  const mainContent = showSuccess ? (
    <div className="app-main">
      <SuccessContent
        bookingData={bookingData}
        confirmedAmount={confirmedAmount}
        preservedPreOrderItems={preservedPreOrderItems}
        confirmedEmail={confirmedEmail}
        shouldHidePrices={shouldHidePrices}
        isFromManageYourOrder={!!isFromManageYourOrder}
        signature={signature}
        manageYourOrderUrl={manageYourOrderUrl}
      />
    </div>
  ) : (
    <PreOrderMenuView
      showConfirmClientDetails={showConfirmClientDetails}
      showDietaryModal={showDietaryModal}
      setShowDietaryModal={setShowDietaryModal}
      dietarySave={dietarySave}
      detailsConfirmed={detailsConfirmed}
      setDetailsConfirmed={setDetailsConfirmed}
      customerPreOrder={customerPreOrder}
      setCustomerPreOrder={setCustomerPreOrder}
      bookingData={bookingData}
      forNames={forNames}
      multipleNames={multipleNames}
      editNames={editNames}
      addName={addName}
      addNameError={addNameError}
      namesExceedGuests={showNamesExceedGuests}
      handleMultipleNamesOptionChange={handleMultipleNamesOptionChange}
      handleAddName={handleAddName}
      setAddName={setAddName}
      handleValidationChange={handleValidationChange}
      setMenuLimits={setMenuLimits}
      externalValidationErrors={externalValidationErrors}
      savePreorders={savePreorders}
      paidByGroupMember={paidByGroupMember}
      canShowStripeConnect={canShowStripeConnect}
      handlePaymentSuccess={handlePaymentSuccess}
      confirmDisabled={isCacheExpired}
      t={t}
    />
  );

  const headerSection = showSuccess ? null : (
    <StepHeader
      venueName={bookingData.venue.title}
      currentStage={currentStage}
      steps={placeOrderSteps}
      bookingDate={formattedDate}
      bookingTime={formattedTime}
      covers={formattedCovers}
      bookingTypeName={bookingData.bookingTypeName}
      onBack={onBack}
    />
  );

  return (
    <PreorderProvider value={preorderContextValue}>
      <Layout
        showHeader
        headerChildren={mobileStepNav}
        main={
          <>
            {headerSection}
            {mainContent}
          </>
        }
        mainClassName={mainClassName}
        asideClassName="basket-aside"
        aside={asideValue}
        footer={null}
      />
    </PreorderProvider>
  );
};

export { PlaceYourOrder };
